import React, { useCallback, useEffect, useRef, useState } from 'react'
import { InputField, Modal, RadioGroup, Select } from '@enamelai/design-system'
import { buildTimeOptionsAmPm } from '@utils/phoneManagement.utils'
import { AddOverrideFormData } from '../openingHours.data'
import ModalDateField from '../ModalDateField.component'
import './AddOverrideStyle.css'

const TIME_OPTIONS = buildTimeOptionsAmPm(30).map((time) => ({
  value: time,
  label: time,
}))

const OVERRIDE_TYPE_OPTIONS = [
  { value: 'adjusted', label: 'Adjusted hours' },
  { value: 'closed', label: 'Closed' },
]

const emptyForm = (): AddOverrideFormData => ({
  description: '',
  date: '',
  overrideType: 'adjusted',
  startTime: '8:00 AM',
  endTime: '6:00 PM',
})

type AddOverrideProps = {
  isOpen: boolean
  mode?: 'add' | 'edit'
  initialValues?: AddOverrideFormData | null
  onRequestClose: () => void
  onSubmit?: (data: AddOverrideFormData) => void | Promise<void>
  isSubmitting?: boolean
}

const AddOverride: React.FC<AddOverrideProps> = ({
  isOpen,
  mode = 'add',
  initialValues = null,
  onRequestClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<AddOverrideFormData>(emptyForm())
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})
  const wasModalOpenRef = useRef(false)

  useEffect(() => {
    const justOpened = isOpen && !wasModalOpenRef.current
    wasModalOpenRef.current = isOpen
    if (!justOpened) return
    setFormData(initialValues ? { ...initialValues } : emptyForm())
    setValidationErrors({})
  }, [isOpen, initialValues])

  const handleInputChange = useCallback(
    <K extends keyof AddOverrideFormData>(
      field: K,
      value: AddOverrideFormData[K],
    ): void => {
      setFormData((prev) => ({ ...prev, [field]: value }))
      setValidationErrors((prev) => {
        if (!prev[field]) return prev
        const next = { ...prev }
        delete next[field]
        return next
      })
    },
    [],
  )

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {}
    if (!formData.description.trim()) {
      errors.description = 'Description is required'
    }
    if (!formData.date.trim()) {
      errors.date = 'Date is required'
    }
    if (formData.overrideType === 'adjusted') {
      if (!formData.startTime.trim()) {
        errors.startTime = 'Start time is required'
      }
      if (!formData.endTime.trim()) {
        errors.endTime = 'End time is required'
      }
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [formData])

  const handleClose = useCallback((): void => {
    setFormData(emptyForm())
    setValidationErrors({})
    onRequestClose()
  }, [onRequestClose])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!validate()) return

    const payload: AddOverrideFormData = {
      description: formData.description.trim(),
      date: formData.date,
      overrideType: formData.overrideType,
      startTime: formData.overrideType === 'adjusted' ? formData.startTime : '',
      endTime: formData.overrideType === 'adjusted' ? formData.endTime : '',
    }

    try {
      await onSubmit?.(payload)
      handleClose()
    } catch {
      // Parent shows error toast; keep modal open for corrections.
    }
  }, [formData, handleClose, onSubmit, validate])

  const showTimeFields = formData.overrideType === 'adjusted'

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="md"
      title={mode === 'edit' ? 'Edit override' : 'Add overrides'}
      closeOnBackdrop={false}
      className="oh-modal"
      primaryLabel={mode === 'edit' ? 'Save' : 'Add'}
      primaryDisabled={isSubmitting}
      onPrimaryClick={() => {
        void handleSave()
      }}
      secondaryLabel="Cancel"
      onSecondaryClick={handleClose}
    >
      <div className="oh-modal-form">
        <InputField
          fullWidth
          variant="text"
          label="Description"
          placeholder="e.g. Staff event"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          error={!!validationErrors.description}
          helperText={validationErrors.description}
        />
        <ModalDateField
          label="Date"
          value={formData.date}
          onChange={(value) => handleInputChange('date', value)}
          error={validationErrors.date}
        />
        <RadioGroup
          name="ohOverrideType"
          label="Override type"
          orientation="horizontal"
          options={OVERRIDE_TYPE_OPTIONS}
          value={formData.overrideType}
          onChange={(value) =>
            handleInputChange(
              'overrideType',
              value as AddOverrideFormData['overrideType'],
            )
          }
        />
        {showTimeFields && (
          <div className="oh-modal-row">
            <Select
              label="Start time"
              options={TIME_OPTIONS}
              value={formData.startTime}
              error={!!validationErrors.startTime}
              helperText={validationErrors.startTime}
              onChange={(value) =>
                handleInputChange('startTime', String(value))
              }
            />
            <Select
              label="End time"
              options={TIME_OPTIONS}
              value={formData.endTime}
              error={!!validationErrors.endTime}
              helperText={validationErrors.endTime}
              onChange={(value) => handleInputChange('endTime', String(value))}
            />
          </div>
        )}
      </div>
    </Modal>
  )
}

export default AddOverride
